import { Carousel, Embla } from "@mantine/carousel";
import { NumberFormatter } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { Trash2 } from "lucide-react";
import { useGetLoyaltyConfigQuery } from "features/admin/admin.api";
import { useCartSync } from "hooks/useCartSync";
import { useBulkStock } from "hooks/useBulkStock";
import CircleLoader from "Components/Common/CircleLoader";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEcomConfig } from "hooks/useEcomConfig";
import {
  useGetAllCartWithIdQuery,
  useRemoveAllFromCartMutation,
} from "Services/CartApiSlice";
import {
  useGetCustomerLoyaltyQuery,
  useGetBillingAddressQuery,
} from "Services/CustomerApiSlice";
import { useGetPickupLocationsQuery } from "Services/Admin/ShipmentConfigApiSlice";
import { useCalculateShippingCostQuery } from "Services/DelhiveryApiSlice";
import {
  useGetProductByIdQuery,
} from "Services/ProductApiSlice";
import { useGetAllOverallPromotionsQuery } from "Services/Admin/EcommerceDiscountApiSlice";
import {
  addToProductCart,
  calculateDiscount,
  calculateSubTotal,
  clearCart,
  clearReward,
  setLoyaltyConfig,
  setRewardApplied,
  setSelectedGrandPromo,
} from "State/CartSlice/CartSlice";
import { AppDispatch, RootState } from "State/store";
import CartItemCard from "Templates/Template4/Components/Common/CartItemCard";
import { Cart, CustomerLoyaltyDTO } from "Types/Cart";
import { getBaseCurrency } from "Utilities/CurrencyHandler";
import { formatIndianCurrency, formatIndianNumber } from "Utilities/CurrencyFormatter";
import { fetchWithRetry } from "Utilities/FetchWithRetry";
import { isPromotionActive } from "Utilities/PromotionDateUtils";
import OrderDiscountSelector from "Templates/Template4/Components/Common/OrderDiscountSelector";
import { useLiveTime } from "hooks/useLiveTime";
import { isClientPromotionActive, isPromoUsageLimitReached } from "Utilities/PromotionDateUtils";


interface MiniCartSidebarProps {
  open: boolean;
  onClose: () => void;
}

const MiniCartSidebar: React.FC<MiniCartSidebarProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const dispatch: AppDispatch = useDispatch();
  // On checkout/order pages, PriceDetails already mounts its own OrderDiscountSelector.
  // Mounting a second instance causes the two auto-select effects to fight over the same
  // Redux state (selectedCartPromoId / selectedGrandPromo) and override each other.
  const isOnCheckoutPage = pathname.startsWith("/checkout") || pathname.startsWith("/order-placed");

  useCartSync(!open);

  // Removed cartProductsResult from here to be placed below cartList declaration
  const { data: productData, refetch: refetchProduct } = useGetProductByIdQuery(
    { productId: "" },
    { skip: true },
  );
  const [removeAllFromCart] = useRemoveAllFromCartMutation();

  const [loyaltyOffers, setLoyaltyOffers] = useState<any[]>([]);
  const [loadingOffers, setLoadingOffers] = useState(false);

  const loggedInCarouselRef = useRef<Embla | null>(null);
  const nonLoggedInCarouselRef = useRef<Embla | null>(null);

  const {
    cartList,
    cartProductList,
    cartSubTotal,
    cartDiscount,
    rewardApplied,
    rewardValue,
    loyaltyMaxRedeemPercentage,
    loyaltyRedeemValue,
    selectedGrandPromo,
    selectedCartPromoId,
  } = useSelector((state: RootState) => state.cart);

  const token = useSelector((state: RootState) => state.login.token);
  const selectedAddress = useSelector(
    (state: RootState) => state.customer.selectedAddress,
  );

  const rewardSavings = rewardApplied ? Math.min(rewardValue, cartDiscount) : 0;
  const baseFinalTotal = Math.max(0, cartDiscount - rewardSavings);

  const {
    ecomConfig,
    numberOfDecimal,
    allowLTAVAILINVQTY,
    isLoyaltyPointsEnabled,
    showPromotion,
  } = useEcomConfig();

  const { data: grandDiscountData } = useGetAllOverallPromotionsQuery(undefined, {
    skip: !showPromotion,
  });

  let grandDiscountAmount = 0;
  const discountResults = grandDiscountData ?? [];

  const grandPromo = selectedGrandPromo;

  if (
    showPromotion &&
    grandPromo &&
    grandPromo.promotionValue &&
    baseFinalTotal >= (grandPromo.fromPrice || 0) &&
    baseFinalTotal <= (grandPromo.toPrice || Infinity) &&
    grandPromo.isActive === "Y"
  ) {
    if (grandPromo.promotionType === "%") {
      grandDiscountAmount = (baseFinalTotal * grandPromo.promotionValue) / 100;
    } else if (
      grandPromo.promotionType === "INR" &&
      grandPromo.promotionValue < baseFinalTotal
    ) {
      grandDiscountAmount = grandPromo.promotionValue;
    }
  }

  const finalTotal = Math.max(0, baseFinalTotal - grandDiscountAmount);

  const isShippingChargesApplicable =
    ecomConfig?.isShippingChargesApplicable ?? false;
  const shippingThreshold = ecomConfig?.shippingChargeThresholdAmount ?? 0;

  const cartItemIdsKey = cartList.map((c: Cart) => c.productId).join(",");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const cartItemIds = useMemo(
    () => cartList.map((c: Cart) => c.productId),
    [cartItemIdsKey],
  );

  const products = cartProductList?.products || [];
  const productTypeByItem = useMemo(() => {
    const map: Record<string, string | undefined> = {};
    products.forEach((p: any) => {
      map[p.product.item] = p.product.productType;
    });
    return map;
  }, [products]);
  const cartStockItems = useMemo(
    () => cartItemIds.map((id: string) => ({ item: id, productType: productTypeByItem[id] })),
    [cartItemIds, productTypeByItem],
  );

  // Skip until cartProductList has loaded the matching products, otherwise the first fetch
  // fires against the wrong endpoint (regular vs record) before item productTypes are known.
  const availableQtyMap = useBulkStock(
    cartStockItems,
    !open || (cartItemIds.length > 0 && products.length === 0),
  );
  const {
    data: cartProductsResult,
    isLoading: cartLoading,
    isFetching: cartFetching,
  } = useGetAllCartWithIdQuery(
    { cartItems: cartItemIds },
    {
      skip: !open || cartItemIds.length === 0,
      refetchOnMountOrArgChange: true,
    },
  );

  const { data: loyaltyInfoData, refetch: refetchLoyaltyInfo } =
    useGetCustomerLoyaltyQuery(undefined, {
      skip: !token || !isLoyaltyPointsEnabled,
    });

  const { data: billingAddressData } = useGetBillingAddressQuery(
    { plant: process.env.REACT_APP_PLANT ?? "" },
    {
      skip: !token,
    },
  );

  const { data: loyaltyConfigData } = useGetLoyaltyConfigQuery();

  const applyShipping =
    isShippingChargesApplicable && (shippingThreshold <= 0 || finalTotal < shippingThreshold);
  const shippingDeliveryPin =
    selectedAddress?.pinCode || billingAddressData?.results?.pinCode;
  const { data: pickupLocations } = useGetPickupLocationsQuery();
  const shippingOriginPin = pickupLocations?.find(
    (loc: any) => loc.active === true,
  )?.pinCode;
  const shippingWeight =
    cartList.reduce(
      (total: number, item: any) => total + (item.quantity || 0),
      0,
    ) * 400;
  const { data: shippingCostData } = useCalculateShippingCostQuery(
    {
      billingMode: "S",
      paymentMode: "Pre-paid",
      status: "DTO",
      weight: shippingWeight,
      destinationPinCode: shippingDeliveryPin!,
      originPinCode: shippingOriginPin!,
    },
    {
      skip: !shippingDeliveryPin || !shippingOriginPin || shippingWeight === 0,
    },
  );
  const shippingCost = shippingCostData?.[0]?.total_amount ?? 0;
  const effectiveShippingCost =
    applyShipping && !!shippingDeliveryPin ? shippingCost : 0;

  const now = useLiveTime();

  const promoItemIds = useMemo(() => {
    if (!showPromotion) return [];
    const ids = new Set<string>();
    products.forEach((product: any) => {
      (product.promotions || []).forEach((p: any) => {
        if (isClientPromotionActive(p, now) && p.promotionBy === "ByQuantity" && p.getItem) ids.add(p.getItem);
      });
    });
    return Array.from(ids);
  }, [products, showPromotion, now]);

  const promoItemStockMap = useBulkStock(promoItemIds, !open);

  const allCartPromotions = useMemo(() => {
    if (!showPromotion) return [];
    // Use a Map to collect all source products per promo so we can show:
    //   - which product to buy more of (unlock hint)
    //   - total savings across all applicable items (discount display)
    const promoMap = new Map<string, any>();
    cartList.forEach((item: any) => {
      const product = products.find((p: any) => p.product.item === item.productId);
      if (!product) return;
      if ((product.product.stockQty ?? 0) <= 0) return;
      (product.promotions || [])
        .filter((p: any) => {
          if (!isClientPromotionActive(p, now)) return false;
          if (isPromoUsageLimitReached(p)) return false;
          if (p.promotionBy === "ByValue") {
            return typeof p.buyQty === "number" ? item.quantity >= p.buyQty : true;
          }
          return p.promotionBy === "ByQuantity";
        })
        .forEach((promo: any) => {
          const key = String(promo.promotionId || promo.id);
          if (!promoMap.has(key)) {
            promoMap.set(key, { ...promo, _sourceProducts: [], _totalQty: 0 });
          }
          const entry = promoMap.get(key);
          entry._sourceProducts.push({
            name: product.product.title || product.product.itemDesc || "",
            categoryName: product.product.categoryName || "",
            id: item.productId,
            currentQty: item.quantity,
            requiredQty: promo.buyQty || 0,
          });
          entry._totalQty += item.quantity;
        });
    });
    return Array.from(promoMap.values());
  }, [cartList, products, now, showPromotion]);

  // Dedicated map so OrderDiscountSelector can name the exact product in the "unlock" hint.
  // Keyed by promoKey → all cart products that carry that promo + their current quantity.
  const promoSourceMap = useMemo(() => {
    if (!showPromotion) return {} as Record<string, Array<{ name: string; categoryName?: string; currentQty: number }>>;
    const map: Record<string, Array<{ name: string; categoryName?: string; currentQty: number }>> = {};
    cartList.forEach((item: any) => {
      const product = products.find((p: any) => p.product.item === item.productId);
      if (!product || (product.product.stockQty ?? 0) <= 0) return;
      (product.promotions || [])
        .filter((p: any) => isClientPromotionActive(p, now) && !isPromoUsageLimitReached(p))
        .forEach((promo: any) => {
          const key = String(promo.promotionId || promo.id);
          if (!map[key]) map[key] = [];
          map[key].push({
            name: product.product.title || product.product.itemDesc || "",
            categoryName: product.product.categoryName || "",
            currentQty: item.quantity,
          });
        });
    });
    return map;
  }, [cartList, products, now, showPromotion]);

  // Returns free items (and out-of-stock free item IDs) for a single cart item,
  // restricted to the applied promotion so only the selected promo's free books show.
  const getItemPromoFreeProducts = (product: any, item: any) => {
    const byQuantityPromotionProducts: { id: string; qty: number }[] = [];
    const outOfStockPromoItemIds: string[] = [];
    if (!showPromotion) return { byQuantityPromotionProducts, outOfStockPromoItemIds };

    const byQuantityPromos = (product.promotions || []).filter(
      (p: any) => isClientPromotionActive(p, now) && p.promotionBy === "ByQuantity",
    );

    for (const promo of byQuantityPromos) {
      if (!promo || typeof promo.buyQty !== "number" || !promo.getItem || promo.buyQty > item.quantity) continue;
      // Only show free books from the applied/selected promotion.
      if (selectedCartPromoId && String(promo.promotionId || promo.id) !== selectedCartPromoId) continue;
      if (allowLTAVAILINVQTY === 0 && promoItemStockMap[promo.getItem] !== undefined && promoItemStockMap[promo.getItem] === 0) {
        outOfStockPromoItemIds.push(promo.getItem);
      } else {
        const freeQty = Math.floor(item.quantity / promo.buyQty) * promo.getQty;
        byQuantityPromotionProducts.push({ id: promo.getItem, qty: freeQty });
      }
    }

    const combined = Object.values(
      byQuantityPromotionProducts.reduce<Record<string, { id: string; qty: number }>>(
        (acc, { id, qty }) => {
          acc[id] = acc[id] ? { id, qty: acc[id].qty + qty } : { id, qty };
          return acc;
        },
        {},
      ),
    );

    return { byQuantityPromotionProducts: combined, outOfStockPromoItemIds: Array.from(new Set(outOfStockPromoItemIds)) };
  };

  const byQuantityPromoItemsMap = useMemo(() => {
    if (!showPromotion) return {} as Record<string, { id: string; qty: number }[]>;
    const result: Record<string, { id: string; qty: number }[]> = {};
    cartList.forEach((item: any) => {
      const product = products.find((p: any) => p.product.item === item.productId);
      if (!product) return;
      if ((product.product.stockQty ?? 0) <= 0) return;
      const byQtyPromos = (product.promotions || [])
        .filter((p: any) => isClientPromotionActive(p, now) && p.promotionBy === "ByQuantity");
      for (const promo of byQtyPromos) {
        if (!promo.getItem || typeof promo.buyQty !== "number" || promo.buyQty > item.quantity) continue;
        if (allowLTAVAILINVQTY === 0 && promoItemStockMap[promo.getItem] !== undefined && promoItemStockMap[promo.getItem] === 0) continue;
        const freeQty = Math.floor(item.quantity / promo.buyQty) * promo.getQty;
        const promoKey = String(promo.promotionId);
        if (!result[promoKey]) result[promoKey] = [];
        const existing = result[promoKey].find((i) => i.id === promo.getItem);
        if (existing) { existing.qty += freeQty; } else { result[promoKey].push({ id: promo.getItem, qty: freeQty }); }
      }
    });
    return result;
  }, [cartList, products, now, showPromotion, allowLTAVAILINVQTY, promoItemStockMap]);

  // Promos where the quantity threshold IS met but the usage limit is exceeded.
  // These are shown inline in the sidebar so the user doesn't need to open the modal to find out.
  const usageLimitExceededPromos = useMemo(() => {
    if (!showPromotion) return [];
    return allCartPromotions.filter((promo: any) => {
      if (promo.availableUsage === undefined || promo.availableUsage === null) return false;
      const buyQty = Number(promo.buyQty);
      if (!buyQty || buyQty <= 0) return false;

      if (promo.promotionBy === "ByQuantity") {
        // Only warn when the buy threshold is actually met
        const promoKey = String(promo.promotionId || promo.id);
        if (!byQuantityPromoItemsMap[promoKey]) return false;
        return Math.floor((promo._totalQty || 0) / buyQty) > promo.availableUsage;
      }

      if (promo.promotionBy === "ByValue") {
        // allCartPromotions already filters ByValue to threshold-met promos
        return Math.floor((promo._totalQty || 0) / buyQty) > promo.availableUsage;
      }

      return false;
    });
  }, [allCartPromotions, byQuantityPromoItemsMap, showPromotion]);

  useEffect(() => {
    if (cartProductsResult && open) {
      dispatch(addToProductCart(cartProductsResult));
    }
  }, [cartProductsResult, open, dispatch]);

  useEffect(() => {
    const fetchLoyaltyOffers = async () => {
      if (!loyaltyConfigData?.results) return;

      setLoadingOffers(true);
      const offers: any[] = [];
      const today = new Date();

      if (
        loyaltyConfigData.results.byPriceEnabled &&
        loyaltyConfigData.results.loyaltyByPrices
      ) {
        const priceRules = loyaltyConfigData.results.loyaltyByPrices;

        priceRules.forEach((rule: any) => {
          if (loyaltyConfigData.results.byPriceScheduled) {
            if (!rule.fromDate || !rule.toDate) return;
            if (isPromotionActive(today, rule.fromDate, rule.toDate)) {
              offers.push({
                type: "price",
                id: rule.id,
                title: `Get ${rule.points} reward points`,
                description: `for orders above ₹${rule.price}`,
                points: rule.points,
                price: rule.price,
                fromDate: rule.fromDate,
                toDate: rule.toDate,
              });
            }
          } else {
            offers.push({
              type: "price",
              id: rule.id,
              title: `Get ${rule.points} reward points`,
              description: `for orders above ₹${rule.price}`,
              points: rule.points,
              price: rule.price,
            });
          }
        });
      }

      if (
        loyaltyConfigData.results.byProductEnabled &&
        loyaltyConfigData.results.loyaltyByProducts
      ) {
        const productRules = loyaltyConfigData.results.loyaltyByProducts;

        productRules.forEach((rule: any) => {
          if (loyaltyConfigData.results.byProductScheduled) {
            if (!rule.fromDate || !rule.toDate) return;
            if (isPromotionActive(today, rule.fromDate, rule.toDate)) {
              offers.push({
                type: "product",
                id: rule.id,
                item: rule.item,
                title: `Get ${rule.points} reward points`,
                description: `Purchase this product to earn ${rule.points} points`,
                points: rule.points,
                productName: "",
                fromDate: rule.fromDate,
                toDate: rule.toDate,
                productLink: `/product?pid=${rule.item}`,
              });
            }
          } else {
            offers.push({
              type: "product",
              id: rule.id,
              item: rule.item,
              title: `Get ${rule.points} reward points`,
              description: `for purchasing this product`,
              points: rule.points,
              productName: "",
            });
          }
        });
      }

      setLoyaltyOffers(offers);
      setLoadingOffers(false);
    };

    fetchLoyaltyOffers();
  }, [loyaltyConfigData]);

  useEffect(() => {
    if (!loyaltyInfoData) return;

    dispatch(
      setLoyaltyConfig({
        maxRedeemPercentage: loyaltyInfoData.results.maxRedeem,
        redeemValue:
          loyaltyInfoData.results.currentPoints *
          (loyaltyInfoData.results.redeemPrice /
            loyaltyInfoData.results.redeemPoints),
      }),
    );
  }, [loyaltyInfoData, dispatch]);

  const hasFetchedProductNames = useRef(false);

  useEffect(() => {
    const fetchProductNames = async () => {
      if (hasFetchedProductNames.current) return;

      const productOffers = loyaltyOffers.filter(
        (offer) => offer.type === "product",
      );

      if (productOffers.length === 0) return;

      const updatedOffers = [...loyaltyOffers];
      let hasChanges = false;

      for (const offer of productOffers) {
        try {
          if (offer.productName) continue;

          const product = products.find(
            (p: any) => p.product.item === offer.item,
          );

          if (product?.product?.itemDesc) {
            const index = updatedOffers.findIndex((o) => o.id === offer.id);
            if (index !== -1) {
              updatedOffers[index] = {
                ...updatedOffers[index],
                productName: product.product.itemDesc,
              };
              hasChanges = true;
            }
          }
        } catch (error) {
          console.error(`Failed to fetch product ${offer.item}:`, error);
        }
      }

      if (hasChanges) {
        setLoyaltyOffers(updatedOffers);
        hasFetchedProductNames.current = true;
      }
    };

    if (loyaltyOffers.length > 0) {
      fetchProductNames();
    }
  }, [loyaltyOffers, products]);

  const [loyaltyInfo, setLoyaltyInfo] = useState<CustomerLoyaltyDTO>({
    customerNo: "",
    pointsRedeemed: 0,
    pointsReturned: 0,
    redeemPrice: 1,
    redeemPoints: 1,
    currentPoints: 0,
    maxRedeem: 0,
  });

  useEffect(() => {
    if (loyaltyInfoData) {
      setLoyaltyInfo(loyaltyInfoData.results);
    }
  }, [loyaltyInfoData]);

  useEffect(() => {}, [rewardApplied, loyaltyInfo]);

  useEffect(() => {
    if (open && token && isLoyaltyPointsEnabled) {
      refetchLoyaltyInfo();
    }
  }, [open, token, isLoyaltyPointsEnabled, refetchLoyaltyInfo]);

  const activePriceRule = (() => {
    if (!loyaltyConfigData?.results?.byPriceEnabled) return null;

    const rules = loyaltyConfigData.results.loyaltyByPrices || [];
    const today = new Date();

    return (
      rules.find((rule: any) => {
        if (loyaltyConfigData.results.byPriceScheduled) {
          if (!rule.fromDate || !rule.toDate) return false;
          return isPromotionActive(today, rule.fromDate, rule.toDate);
        }
        return true;
      }) || null
    );
  })();

  useEffect(() => {
    if (open && window.innerWidth < 768) {
      onClose();
      navigate("/cart");
    }
  }, [open, navigate, onClose]);

  useEffect(() => {
    // Redundant manual fetch replaced by declarative useGetAllCartWithIdQuery
  }, [open, cartList, dispatch]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const bagDiscountValue = Math.abs(cartSubTotal - cartDiscount);
  const hasRewardValue = loyaltyRedeemValue > 0;

  useEffect(() => {
    dispatch(calculateSubTotal());
    dispatch(calculateDiscount());
  }, [cartList, cartProductList, dispatch, now]);

  const goToCart = () => {
    onClose();
    navigate("/cart");
  };

  const goToCheckout = () => {
    if (hasOutOfStockItems) {
      return;
    }

    onClose();
    navigate("/checkout");
  };

  const applyReward = () => {
    if (!token) {
      onClose();
      navigate("/login");
      return;
    }

    if (!hasRewardValue) {
      return;
    }

    const displayRewardValue = Math.min(
      (cartDiscount * loyaltyMaxRedeemPercentage) / 100,
      loyaltyRedeemValue,
    );

    dispatch(setRewardApplied({ applied: true }));
  };

  const removeReward = () => {
    dispatch(clearReward());
  };

  const handleClearCart = async () => {
    dispatch(clearCart());
    dispatch(clearReward());
    dispatch(setSelectedGrandPromo(null));

    if (token) {
      try {
        const res = await removeAllFromCart().unwrap();
        notifications.show({ message: res.message || "Cart cleared", color: "red" });
      } catch (error: any) {
        notifications.show({
          message: error?.data?.message || "Failed to clear cart",
          color: "red",
        });
      }
    } else {
      notifications.show({ message: "Cart cleared", color: "red" });
    }
  };

  const unavailableItemCount = useMemo(() => {
    return cartList.filter((item: any) => {
      const availQty = availableQtyMap[item.productId];
      if (availQty === undefined) return false;
      return allowLTAVAILINVQTY === 0 && (availQty === 0 || item.quantity > availQty);
    }).length;
  }, [cartList, availableQtyMap, allowLTAVAILINVQTY]);

  const hasOutOfStockItems = unavailableItemCount > 0;

  if (!open) return null;

  const itemCount = cartList?.reduce(
    (total: number, item: any) => total + (item.quantity || 0),
    0,
  );

  const getByQuantityPromotionProducts = (
    promotions: any[],
    quantity: number,
  ): { id: string; qty: number }[] => {
    if (!showPromotion || !promotions?.length) return [];

    return promotions
      .filter(
        (p) =>
          p.promotionBy === "ByQuantity" &&
          typeof p.buyQty === "number" &&
          p.buyQty <= quantity &&
          p.getItem,
      )
      .map((promo) => ({
        id: promo.getItem,
        qty: Math.floor(quantity / promo.buyQty) * promo.getQty,
      }));
  };

  if (window.innerWidth < 768) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[999]"
        onClick={onClose}
      />

      <aside
        data-aos="fade-left"
        className="
          fixed top-0 right-0 
          h-full 
          w-[400px] 
          max-w-md  
          bg-vintageBg text-vintageText font-gilroyRegular leading-wider
          shadow-2xl z-[1000] 
          flex flex-col 
          transition-all duration-300 ease-in-out "
      >
        <div className="flex items-center justify-between px-4 py-4 border-b border-vintageBorder shrink-0">
          <button
            onClick={onClose}
            className="flex items-center gap-2 text-sm font-semibold text-vintageText hover:opacity-90 transition"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
            <span>Continue Shopping</span>
          </button>

          <div className="flex items-center gap-3">
            {cartList.length > 0 && (
              <button
                onClick={handleClearCart}
                className="flex items-center gap-1 text-sm font-semibold text-red-600 hover:text-red-700 transition-colors"
              >
                <Trash2 size={16} />
                Clear
              </button>
            )}

            <button
              onClick={goToCart}
              className="
                bg-yellow-500 text-vintageBg
                py-2 px-4 rounded-lg
                font-semibold flex items-center gap-2
                shadow-md hover:bg-opacity-90 transition
              "
            >
              <span>Cart</span>

              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="5 12 5 5 12 5" />
                <polyline points="19 12 19 19 12 19" />
                <line x1="5" y1="5" x2="19" y2="19" />
              </svg>
            </button>
          </div>
        </div>

        {cartList.length === 0 && (
          <div className="flex-1 flex items-center justify-center px-4 py-8">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-vintageText mb-2">
                📚 Your cart is empty
              </h3>
              <p className="text-sm text-gray-700 mb-4">
                Add products to your bag and they'll appear here.
              </p>

              <button
                onClick={() => {
                  onClose();
                  navigate("/");
                }}
                className="mt-2 border border-vintageText text-black bg-transparent px-4 py-2 rounded hover:bg-vintageText hover:text-white transition"
              >
                Continue Shopping
              </button>
            </div>
          </div>
        )}

        {cartList.length > 0 && (
          <div className="flex-1 flex flex-col overflow-hidden">
            {cartLoading ? (
              <div className="flex-1 flex items-center justify-center">
                <CircleLoader />
              </div>
            ) : (
              <div className="flex-1 flex flex-col overflow-hidden">
                <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 scrollbar-hide hide-scrollbar">
                  {isLoyaltyPointsEnabled && token && (
                    <div className="p-3 bg-vintageBg rounded-lg border border-vintageBorder flex justify-between items-center gap-1">
                      <div>
                        <div>
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-sm font-semibold">
                              {formatIndianNumber(
                                loyaltyInfo.currentPoints,
                                numberOfDecimal,
                              )}{" "}
                              Reward Points
                            </p>
                            <p className="text-sm font-semibold text-gray-700">
                              ({formatIndianCurrency(loyaltyRedeemValue, numberOfDecimal)})
                            </p>
                          </div>
                          <p className="text-xs text-gray-600 mt-1">
                            Claim up to {loyaltyMaxRedeemPercentage}%
                          </p>

                          {rewardApplied && (
                            <p className="text-green-600 text-xs font-semibold mt-2">
                              ✓ Applied
                            </p>
                          )}
                        </div>
                      </div>

                      {!rewardApplied ? (
                        <button
                          onClick={applyReward}
                          disabled={!hasRewardValue}
                          className={`px-3 py-1 rounded text-sm ${
                            hasRewardValue
                              ? "border border-vintageText text-vintageText bg-transparent hover:bg-vintageText hover:text-white"
                              : "bg-gray-300 text-gray-500 cursor-not-allowed"
                          }`}
                        >
                          Apply
                        </button>
                      ) : (
                        <button
                          onClick={removeReward}
                          className="bg-red-600 text-white px-3 py-1 rounded text-sm"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  )}

                  {isLoyaltyPointsEnabled &&
                    loyaltyOffers.length > 0 &&
                    token && (
                      <div className="mt-4">
                        <div className="px-3 py-2 rounded-md bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-xs font-semibold text-yellow-800 flex items-center gap-1">
                              <span>🎁</span> Available Offers
                            </h4>
                            {loyaltyOffers.length > 1 && (
                              <div className="flex gap-1">
                                <button
                                  className="w-6 h-6 flex items-center justify-center bg-yellow-800 text-white rounded text-xs"
                                  onClick={() =>
                                    loggedInCarouselRef.current?.scrollPrev()
                                  }
                                >
                                  ‹
                                </button>
                                <button
                                  className="w-6 h-6 flex items-center justify-center bg-yellow-800 text-white rounded text-xs"
                                  onClick={() =>
                                    loggedInCarouselRef.current?.scrollNext()
                                  }
                                >
                                  ›
                                </button>
                              </div>
                            )}
                          </div>

                          <Carousel
                            getEmblaApi={(embla) => {
                              loggedInCarouselRef.current = embla;
                            }}
                            withIndicators={false}
                            withControls={false}
                            height={110}
                            slideSize="100%"
                            slideGap="xs"
                            loop
                            align="start"
                            slidesToScroll={1}
                            classNames={{
                              root: "w-full",
                              control: "bg-yellow-800 text-white border-0",
                              indicator: "bg-yellow-300",
                            }}
                          >
                            {loyaltyOffers.map((offer, index) => (
                              <Carousel.Slide key={offer.id || index}>
                                <div className="p-2 bg-white/80 rounded border border-yellow-100 h-full">
                                  <p className="text-xs font-semibold text-gray-800">
                                    {offer.title}
                                  </p>
                                  <div className="flex items-start justify-between gap-2 mt-1">
                                    <p className="text-[11px] text-gray-600 leading-snug">
                                      {offer.description}
                                    </p>
                                    {offer.item && (
                                      <button
                                        onClick={() => {
                                          onClose();
                                          window.scrollTo(0, 0);
                                          navigate(
                                            `/product?pid=${offer.item}`,
                                          );
                                        }}
                                        className="text-[10px] text-blue-600 hover:underline mt-1"
                                      >
                                        View Product →
                                      </button>
                                    )}
                                  </div>
                                  {offer.fromDate && offer.toDate && (
                                    <p className="text-[10px] text-gray-500 mt-1">
                                      Valid:{" "}
                                      {new Date(
                                        offer.fromDate,
                                      ).toLocaleDateString()}{" "}
                                      -{" "}
                                      {new Date(
                                        offer.toDate,
                                      ).toLocaleDateString()}
                                    </p>
                                  )}
                                </div>
                              </Carousel.Slide>
                            ))}
                          </Carousel>
                        </div>
                      </div>
                    )}

                  {isLoyaltyPointsEnabled &&
                    loyaltyOffers.length > 0 &&
                    !token && (
                      <div className="mt-4">
                        <div className="px-3 py-2 rounded-md bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-xs font-semibold text-blue-800 flex items-center gap-1">
                              <span>🎁</span> Login for Exclusive Offers
                            </h4>
                            {loyaltyOffers.length > 1 && (
                              <div className="flex gap-1">
                                <button
                                  className="w-6 h-6 flex items-center justify-center bg-blue-800 text-white rounded text-xs"
                                  onClick={() =>
                                    nonLoggedInCarouselRef.current?.scrollPrev()
                                  }
                                >
                                  ‹
                                </button>
                                <button
                                  className="w-6 h-6 flex items-center justify-center bg-blue-800 text-white rounded text-xs"
                                  onClick={() =>
                                    nonLoggedInCarouselRef.current?.scrollNext()
                                  }
                                >
                                  ›
                                </button>
                              </div>
                            )}
                          </div>

                          <Carousel
                            getEmblaApi={(embla) => {
                              nonLoggedInCarouselRef.current = embla;
                            }}
                            withIndicators={false}
                            withControls={false}
                            height={110}
                            slideSize="100%"
                            slideGap="xs"
                            loop
                            align="start"
                            slidesToScroll={1}
                            classNames={{
                              root: "w-full",
                              control: "bg-blue-800 text-white border-0",
                              indicator: "bg-blue-300",
                            }}
                          >
                            {loyaltyOffers.map((offer, index) => (
                              <Carousel.Slide key={offer.id || index}>
                                <div className="p-2 bg-white/80 rounded border border-blue-100 h-full">
                                  <p className="text-xs font-semibold text-gray-800">
                                    Sign In and {offer.title.toLowerCase()}
                                  </p>
                                  <div className="mt-1">
                                    <p className="text-[11px] text-gray-600">
                                      {offer.description}
                                    </p>
                                    {offer.item && (
                                      <button
                                        onClick={() => {
                                          onClose();
                                          window.scrollTo(0, 0);
                                          navigate(
                                            `/product?pid=${offer.item}`,
                                          );
                                        }}
                                        className="text-[10px] text-blue-600 hover:underline mt-1"
                                      >
                                        View Product →
                                      </button>
                                    )}
                                  </div>
                                  {offer.fromDate && offer.toDate && (
                                    <p className="text-[10px] text-gray-500 mt-1">
                                      Valid:{" "}
                                      {new Date(
                                        offer.fromDate,
                                      ).toLocaleDateString()}{" "}
                                      -{" "}
                                      {new Date(
                                        offer.toDate,
                                      ).toLocaleDateString()}
                                    </p>
                                  )}
                                </div>
                              </Carousel.Slide>
                            ))}
                          </Carousel>
                        </div>
                      </div>
                    )}

                  {isShippingChargesApplicable && shippingThreshold > 0 && (
                    <div className="rounded-md px-3 py-2 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200">
                      {finalTotal >= shippingThreshold ? (
                        <p className="text-xs font-semibold text-emerald-700 flex items-center gap-1">
                          <span>🎉</span> You've unlocked FREE shipping!
                        </p>
                      ) : (
                        <>
                          <p className="text-xs font-semibold text-emerald-800 mb-1">
                            🚚 Add ₹
                            {formatIndianNumber(
                              shippingThreshold - finalTotal,
                              numberOfDecimal,
                            )}{" "}
                            more for FREE shipping!
                          </p>
                          <div className="w-full bg-emerald-100 rounded-full h-1.5">
                            <div
                              className="bg-emerald-500 h-1.5 rounded-full transition-all duration-300"
                              style={{
                                width: `${Math.min(100, (finalTotal / shippingThreshold) * 100)}%`,
                              }}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {!token ? (
                    <div className="border border-vintageBorder rounded-md p-4 bg-vintageBg/60">
                      <p className="text-sm font-semibold mb-2">
                        Get Started & Grab the Best Offers!
                      </p>
                      <button
                        onClick={() => {
                          onClose();
                          navigate("/login");
                        }}
                        className="w-full rounded-sm px-4 py-2 font-semibold border border-vintageText text-vintageText bg-transparent hover:bg-vintageText hover:text-white transition shadow-md"
                      >
                        Sign In / Sign Up
                      </button>
                    </div>
                  ) : (
                    <div className="border border-vintageBorder rounded-md p-4 bg-vintageBg/60">
                      <Link
                        to="/wishlist"
                        onClick={onClose}
                        className="w-full block text-center rounded-sm px-4 py-2 font-semibold bg-pink-50 border border-pink-300 text-pink-600 hover:bg-pink-100 transition-all duration-300"
                      >
                        <span className="flex items-center justify-center gap-2">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                          >
                            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                          </svg>
                          View Wishlist
                        </span>
                      </Link>
                    </div>
                  )}

                  {cartList.map((item) => {
                    const product = products.find(
                      (p: any) => p.product.item === item.productId,
                    );

                    if (!product) return null;

                    const { byQuantityPromotionProducts, outOfStockPromoItemIds } =
                      getItemPromoFreeProducts(product, item);

                    return (
                      <CartItemCard
                        key={`${item.productId}-${item.size || "default"}`}
                        item={product}
                        quantity={item.quantity}
                        token={token}
                        showQuantitySelector={true}
                        showDeleteIcon={true}
                        onClose={onClose}
                        availableQty={availableQtyMap[item.productId]}
                        skipQuery={!open}
                        byQuantityPromotionProducts={byQuantityPromotionProducts}
                        outOfStockPromoItemIds={outOfStockPromoItemIds}
                      />
                    );
                  })}

                  {usageLimitExceededPromos.length > 0 && (
                    <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-300 rounded-lg text-amber-800 text-xs">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mt-0.5 shrink-0">
                        <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
                        <path d="M12 9v4" />
                        <path d="M12 17h.01" />
                      </svg>
                      <div className="space-y-1">
                        {usageLimitExceededPromos.map((promo: any) => (
                          <p key={String(promo.promotionId || promo.id)}>
                            <span className="font-semibold">
                              {promo.promotionName || `Buy ${promo.buyQty} Get ${promo.getQty} Free`}
                            </span>
                            {" "}— usage limit reached (max {promo.availableUsage}). Reduce quantity to apply.
                          </p>
                        ))}
                      </div>
                    </div>
                  )}

                  <OrderDiscountSelector
                    discounts={discountResults}
                    baseFinalTotal={baseFinalTotal}
                    cartPromotions={allCartPromotions}
                    byQuantityPromoItemsMap={byQuantityPromoItemsMap}
                    promoSourceMap={promoSourceMap}
                    disableAutoSelect={isOnCheckoutPage}
                  />

                  <div className="border-t border-vintageBorder pt-4">
                    <h3 className="text-lg font-semibold mb-3">
                      Price Details{" "}
                      {itemCount > 0 && (
                        <span className="text-sm text-gray-700">
                          ({itemCount} {itemCount === 1 ? "Product" : "Products"})
                        </span>
                      )}
                    </h3>

                    <div className="flex justify-between text-sm mb-2">
                      <span>Sub Total</span>
                      <NumberFormatter
                        decimalScale={numberOfDecimal}
                        fixedDecimalScale
                        prefix={getBaseCurrency("INR") || undefined}
                        value={cartSubTotal}
                        thousandSeparator
                        thousandsGroupStyle="lakh"
                      />
                    </div>

                    {bagDiscountValue > 0 && (
                      <div className="flex justify-between text-sm mb-2">
                        <span>Bag Discount</span>
                        <span className="text-green-700">
                          -
                          <NumberFormatter
                            decimalScale={numberOfDecimal}
                            fixedDecimalScale
                            prefix={getBaseCurrency("INR") || undefined}
                            value={bagDiscountValue}
                            thousandSeparator
                            thousandsGroupStyle="lakh"
                          />
                        </span>
                      </div>
                    )}

                    {rewardApplied && (
                      <div className="flex justify-between text-sm mb-2">
                        <span>Reward Savings</span>
                        <span className="text-green-700">
                          -
                          <NumberFormatter
                            decimalScale={numberOfDecimal}
                            fixedDecimalScale
                            prefix={getBaseCurrency("INR") || undefined}
                            value={rewardSavings}
                            thousandSeparator
                            thousandsGroupStyle="lakh"
                          />
                        </span>
                      </div>
                    )}

                    {grandDiscountAmount > 0 && (
                      <div className="flex justify-between text-sm mb-2">
                        <span>Order Discount</span>
                        <span className="text-green-700">
                          -
                          <NumberFormatter
                            decimalScale={numberOfDecimal}
                            fixedDecimalScale
                            prefix={getBaseCurrency("INR") || undefined}
                            value={grandDiscountAmount}
                            thousandSeparator
                            thousandsGroupStyle="lakh"
                          />
                        </span>
                      </div>
                    )}

                    {isShippingChargesApplicable && (
                      <div className="flex justify-between text-sm mb-2">
                        <span>Shipping</span>
                        {shippingDeliveryPin ? (
                          applyShipping ? (
                            <NumberFormatter
                              decimalScale={numberOfDecimal}
                              fixedDecimalScale
                              prefix={getBaseCurrency("INR") || undefined}
                              value={shippingCost}
                              thousandSeparator
                              thousandsGroupStyle="lakh"
                            />
                          ) : (
                            <span className="text-green-700">FREE</span>
                          )
                        ) : (
                          <span className="text-xs text-gray-500 italic">
                            Calculated at checkout
                          </span>
                        )}
                      </div>
                    )}

                    <div className="border-t border-vintageBorder my-3" />

                    <div className="flex justify-between font-semibold text-base">
                      <span>You Pay</span>
                      <span>
                        ₹
                        {formatIndianNumber(
                          finalTotal + effectiveShippingCost,
                          numberOfDecimal,
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="p-4 border-t border-vintageBorder bg-vintageBg shrink-0">
                  {hasOutOfStockItems && (
                    <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-center gap-2 text-red-800 mb-2">
                        {/* <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="8" x2="12" y2="12"></line>
                      <line x1="12" y1="16" x2="12.01" y2="16"></line>
                    </svg> */}
                        <span className="font-semibold">Cannot Checkout</span>
                      </div>
                      <p className="text-sm text-red-700">
                        {unavailableItemCount === 1
                          ? "This product exceeds available stock. Reduce the quantity or remove it to proceed."
                          : `${unavailableItemCount} products exceed available stock. Reduce quantities or remove them to proceed.`}
                      </p>
                    </div>
                  )}

                  <div className="flex items-center justify-between w-full">
                    <div className="flex flex-col">
                      <span className="text-lg font-bold text-vintageText">
                        ₹
                        {formatIndianNumber(
                          finalTotal + effectiveShippingCost,
                          numberOfDecimal,
                        )}
                      </span>
                      <span className="text-xs text-gray-700">Grand Total</span>
                    </div>

                    {unavailableItemCount !== cartList.length && !hasOutOfStockItems && (
                      <button
                        onClick={goToCheckout}
                        className="py-3 px-6 rounded-lg font-semibold flex items-center gap-2 shadow-md transition border border-vintageText text-vintageText bg-transparent hover:bg-vintageText hover:text-white"
                      >
                        <span>Checkout</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </aside>
    </>
  );
};

export default MiniCartSidebar;
